// FP3-035B-1 correction — Eligibility Editor.
//
// Up to 4 eligibility entries per student: 1 primary (required when
// any eligibility is set) + up to 3 additional. Each entry carries:
//   - category: taxonomy key (e.g. "autism")
//   - designation: "primary" | "secondary" | "additional"
//   - subcategories: array of taxonomy subcategory keys
//     (only meaningful for SLD, DD, PSD)
//
// Designation is positional: slot 0 = primary, slot 1 = secondary,
// slots 2-3 = additional. The provider just adds/removes rows; the
// component computes designations from order so admin doesn't need
// to think about "is this the secondary or the additional one?".
//
// Used in three places: AdminAddStudentModal, Caseload AddStudentModal,
// ServiceDemographics.
import { Plus, X } from "@phosphor-icons/react";

const DESIGNATION_FOR_SLOT = (i) => (i === 0 ? "primary" : i === 1 ? "secondary" : "additional");
const SLOT_LABEL = ["Primary eligibility", "Secondary eligibility", "Additional eligibility 3", "Additional eligibility 4"];

export default function EligibilityEditor({
  value = [],
  onChange,
  taxonomy = [],
  testIdPrefix = "elig",
}) {
  // Normalize entries — pad with empty slot if all slots are blank so
  // the form always shows AT LEAST one row.
  const entries = value.length > 0 ? value : [{ category: "", designation: "primary", subcategories: [] }];

  const emit = (next) => {
    // Re-stamp designations positionally so the wire shape stays
    // canonical regardless of UI add/remove order.
    const stamped = next.map((e, i) => ({
      ...e,
      designation: DESIGNATION_FOR_SLOT(i),
    }));
    onChange?.(stamped);
  };

  const updateAt = (idx, patch) => {
    const next = [...entries];
    next[idx] = { ...next[idx], ...patch };
    // Reset subcategories when changing category (subcategory list is
    // category-specific; carrying stale values would be misleading).
    if ("category" in patch && next[idx].category !== entries[idx].category) {
      next[idx].subcategories = [];
    }
    emit(next);
  };

  const removeAt = (idx) => {
    const next = entries.filter((_, i) => i !== idx);
    emit(next.length ? next : [{ category: "", designation: "primary", subcategories: [] }]);
  };

  const addRow = () => {
    if (entries.length >= 4) return;
    emit([...entries, { category: "", designation: DESIGNATION_FOR_SLOT(entries.length), subcategories: [] }]);
  };

  const canAdd = entries.length < 4 && entries[entries.length - 1]?.category;

  return (
    <div data-testid={`${testIdPrefix}-editor`} className="space-y-2">
      {entries.map((entry, idx) => {
        const taxonomyRow = taxonomy.find((c) => c.key === entry.category);
        const subcategories = taxonomyRow?.subcategories || [];
        return (
          <div
            key={idx}
            data-testid={`${testIdPrefix}-row-${idx}`}
            className="rounded-lg border border-[#E5E3DB] bg-[#FBFAF6] p-2.5 space-y-2"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] w-[120px] shrink-0">
                {SLOT_LABEL[idx]}
              </span>
              <select
                value={entry.category || ""}
                onChange={(e) => updateAt(idx, { category: e.target.value })}
                data-testid={`${testIdPrefix}-category-${idx}`}
                className="flex-1 h-8 text-xs border border-[#d4d2cc] rounded px-2 bg-white"
              >
                <option value="">— Select —</option>
                {taxonomy.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}{c.abbr && c.abbr !== "—" ? ` (${c.abbr})` : ""}
                  </option>
                ))}
              </select>
              {entries.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                  className="text-[#9B9890] hover:text-[#B85C5C] shrink-0"
                  aria-label="Remove eligibility"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {/* Subcategory checkboxes — only render when the selected
                category has them (SLD, DD, PSD). */}
            {subcategories.length > 0 && (
              <div data-testid={`${testIdPrefix}-subcats-${idx}`} className="pl-[124px]">
                <p className="text-[9px] uppercase tracking-wider text-[#9B9890] font-semibold mb-1">
                  Areas of impact (check all that apply)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {subcategories.map((s) => {
                    const checked = (entry.subcategories || []).includes(s.key);
                    return (
                      <label
                        key={s.key}
                        data-testid={`${testIdPrefix}-subcat-${idx}-${s.key}`}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border cursor-pointer transition ${
                          checked
                            ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                            : "bg-white text-[#1F2937] border-[#d4d2cc] hover:bg-[#F1EFE8]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const cur = entry.subcategories || [];
                            const next = cur.includes(s.key)
                              ? cur.filter((k) => k !== s.key)
                              : [...cur, s.key];
                            updateAt(idx, { subcategories: next });
                          }}
                          className="hidden"
                        />
                        {s.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        disabled={!canAdd}
        data-testid={`${testIdPrefix}-add`}
        className={`inline-flex items-center gap-1 text-[11px] font-medium ${
          canAdd
            ? "text-[#1E3F33] hover:text-[#173127]"
            : "text-[#9B9890] cursor-not-allowed"
        }`}
      >
        <Plus size={12} weight="bold" /> Add another eligibility
      </button>
    </div>
  );
}


/**
 * Helper for parent forms — derive the legacy single
 * `eligibility_category` string from the new array (first primary
 * entry). Useful for sending both fields during the transition.
 */
export function primaryEligibilityKey(entries = []) {
  const primary = (entries || []).find((e) => e?.designation === "primary" && e?.category);
  return primary?.category || (entries[0]?.category || "");
}
